<template>
  <vs-row>
    <vs-col>
      <vs-row>
        <vs-col class="p-1" vs-lg="6" vs-sm="6" vs-xs="12">
          <guest-count-selector
            :guest-count="booking.guestCount"
            @guest-count-selected="handleGuestCountSelected($event)"
            :disable-component="disableComponent"
          />
        </vs-col>
        <vs-col class="p-1" vs-lg="6" vs-sm="6" vs-xs="12">
          <label class="vs-input--label">Arrival</label>
          <arrival-date-selector
            :arrival-date="booking.arrival"
            @arrival-date-selected="handleArrivalSelected($event)"
            :disable-component="disableComponent"
          />
        </vs-col>
      </vs-row>

      <vs-row class="mt-2">
        <vs-col class="p-1" vs-lg="6" vs-sm="6" vs-xs="12">
          <venue-selector
            :venue-details-id="booking.venueDetailsId"
            @venue-details-selected="handleVenueDetailsSelected($event)"
            :disable-component="disableComponent"
          />
        </vs-col>

        <vs-col class="p-1" vs-lg="6" vs-sm="6" vs-xs="12">
          <section-selector
            :venue-section-id="booking.venueSectionId"
            :venue-sections="
              activeVenueSetup ? activeVenueSetup.venueSections : []
            "
            :loading-sections="loadingSections"
            @venue-section-selected="handleVenueSectionSelected($event)"
            :disable-component="disableComponent"
          />
        </vs-col>
      </vs-row>

      <vs-row class="mt-2" vs-w="12" v-if="!booking.isForEvent()">
        <vs-col class="p-1" vs-lg="6" vs-sm="6" vs-xs="12">
          <label class="vs-input--label">Table</label>
          <div style="margin-left:0.39rem">
          <div v-if="!this.selectedTable" class="text-sm">
            No table selected
          </div>
          <div v-if="this.selectedTable">
            <b>{{this.selectedTable.name}} ({{this.selectedTable.seatCount}} seats)</b>
          </div>
          <div v-if="showTableCombination" class="text-sm mt-2">
            Tables in combination: {{tablePartsString() }}
          </div>
          <div v-if="showMinimumSpend" class="text-sm mt-2">
            Minimum spend: {{formatPrice(selectedMinimumSpendAmount.value/100, selectedMinimumSpendAmount.currency) }}
          </div>  
          </div>
          <div v-if="showNote"
            class="p-2 rounded mb-0 text-yellow-900 text-sm mt-2"
            style="
              background-color: rgb(255, 212, 134) !important;
              color: rgb(139, 93, 10) !important;
            "
          >
            <b>Table note: </b> {{ selectedTable.note }}
          </div>

        </vs-col>
        <vs-col class="p-1 mt-6" vs-lg="6" vs-sm="6" vs-xs="12"
        >
          <table-selector
            :venue-details="selectedVenue"
            :venue-setup="activeVenueSetup"
            :venue-section="selectedSection"

            :scored-tables="availableScoredTables ? availableScoredTables : []"
            @scored-table-selected="handleScoredTableSelected($event)"
            :disable-component="disableComponent"
          />
        </vs-col>
      </vs-row>
    </vs-col>
  </vs-row>
</template>

<script>
import moment from "moment";
import * as _ from "lodash";
import VenueSelector from "./components/VenueSelector.vue";
import SectionSelector from "./components/SectionSelector.vue";
import TableSelector from "./components/TableSelector.vue";
import GuestCountSelector from "./components/GuestCountSelector.vue";
import ArrivalDateSelector from "./components/ArrivalDateSelector.vue";
import { ACTIONS as VENUE_ACTIONS } from "@/store/modules/marcom/marcom";
import { ACTIONS as BOOKING_ACTIONS } from "@/store/modules/marcom/marcom-booking";
import "flatpickr/dist/flatpickr.css";
import { Booking } from "@/store/modules/marcom/data-model/bookings";
import {
  VenueDetails,
  VenueSection,
  VenueSetup,
  VenueTable,
} from "@/store/modules/marcom/data-model/venue-details/index.js";

export default {
  components: {
    VenueSelector,
    SectionSelector,
    TableSelector,
    GuestCountSelector,
    ArrivalDateSelector,
  },
  props: {
    booking: Object,
    initialTable: Object,
    initialTableParts: Array,
    initialMinimumSpendAmount: Object,
    disableComponent: Boolean,
  },
  model: {
    prop: "booking",
    event: "booking-info-updated",
  },
  data() {
    return {
      selectedVenue: undefined,
      activeVenueSetup: undefined,
      selectedSection: undefined,
      loadingSections: false,
      availableScoredTables: [],
      loadingScoredTables: false,
      selectedTable: this.initialTable,
      selectedTableParts: this.initialTableParts,
      selectedMinimumSpendAmount: this.initialMinimumSpendAmount,
    };
  },
  computed: {
    showNote() {
      return !!this.selectedTable &&
            !!this.selectedTable.note;
    },
    showMinimumSpend() {
      return !!this.selectedMinimumSpendAmount;
    },
    showTableCombination() {
      return !!this.selectedTableParts && !!this.selectedTableParts.length > 0;
    }
  },
  methods: {
    async handleGuestCountSelected(guestCount) {
      this.booking.guestCount = guestCount;
      this.availableScoredTables = [];
      if (this.booking.venueSetupId && this.booking.venueSectionId)
        this.availableScoredTables = await this.getAvailableScoredTables();

      this.$emit(
        "booking-info-updated",
        Object.assign(new Booking(), this.booking)
      );
    },
    async handleArrivalSelected(arrival) {
      if (this.booking.arrival !== arrival) {
        this.booking.arrival = arrival;

        await this.updateVenueSetup();

        this.$emit(
          "booking-info-updated",
          Object.assign(new Booking(), this.booking)
        );
      }
    },
    async handleVenueDetailsSelected(venueDetails) {
      if (this.booking.venueDetailsId !== venueDetails.venueDetailsId) {
        this.selectedVenue = Object.assign(new VenueDetails(), venueDetails);
        this.booking.venueDetailsId = venueDetails.venueDetailsId;

        await this.updateVenueSetup();

        this.$emit(
          "booking-info-updated",
          Object.assign(new Booking(), this.booking)
        );
      }
    },
    async handleVenueSectionSelected(venueSection) {
      this.selectedSection = venueSection;
      if (!venueSection) {
        this.booking.venueSectionId = undefined;
      } else {
        this.booking.venueSectionId = venueSection.venueSectionId;
        this.availableScoredTables = await this.getAvailableScoredTables();
      }

      this.$emit(
        "booking-info-updated",
        Object.assign(new Booking(), this.booking)
      );
      this.$emit("section-selected", this.selectedSection);
    },
    handleScoredTableSelected(scoredTable) {
      if (this.booking.venueTableId !== scoredTable.table.venueTableId) {
        this.booking.venueTableId = scoredTable.table.venueTableId;
        this.selectedTable = scoredTable.table;
        this.selectedTableParts = scoredTable.tableParts;
        this.selectedMinimumSpendAmount = scoredTable.minimumSpendAmount

        this.$emit(
          "booking-info-updated",
          Object.assign(new Booking(), this.booking)
        );
      }
    },
    async getActiveSetup() {
      this.loadingSections = true;
      let setup;
      try {
        if (this.booking.arrival && this.booking.venueDetailsId) {
          const date = moment(this.booking.arrival);
          var setupQuery = {
            venueDetailsId: this.booking.venueDetailsId.id,
            dateTime: new Date(date.toJSON()),
          };

          setup = await this.$store.dispatch(
            "marcom/" + VENUE_ACTIONS.GetActiveSetup,
            setupQuery
          );
        }
      } catch (err) {
        console.error(err);
        this.$vs.notify({
          color: "danger",
          title: "Error getting active setup",
          text: "Getting active setup and sections for the chosen date and time failed.",
        });
      } finally {
        this.loadingSections = false;
      }

      return setup;
    },
    async getAvailableScoredTables() {
      if (this.booking.venueSectionId) {
        this.loadingTables = true;

        var availableTableCommand = {
          bookingId: this.booking.id,
          numPersons: this.booking.guestCount,
          arrival: this.booking.arrival,
          venueSectionId: this.booking.venueSectionId.id,
          acceptSoftWarnings: true
        };

        let tables;
        try {
          tables = await this.$store.dispatch(
            "marcomBooking/" + BOOKING_ACTIONS.GetAvailableSectionTables,
            availableTableCommand
          );
        } catch (err) {
          console.error(err);
          this.$vs.notify({
            color: "danger",
            title: "Error getting tables",
            text: "Getting tables for the chosen section, date and time failed.",
          });
        } finally {
          this.loadingTables = false;
        }
        return tables;
      }
      return;
    },
    async updateVenueSetup() {
      this.activeVenueSetup = await this.getActiveSetup();

      if (this.activeVenueSetup) {
        this.selectedSetup = Object.assign(
          new VenueSetup(),
          this.activeVenueSetup
        );
        this.booking.venueSetupId = this.activeVenueSetup.venueSetupId;

        await this.updateVenueSections();
      } else {
        this.selectedSetup = null;
        this.booking.venueSetupId = undefined;
        this.selectedSection = null;
        this.booking.venueSectionId = null;
        this.availableScoredTables = [];
        this.selectedTable = null;
        this.selectedTableParts = null;
        this.selectedMinimumSpendAmount = null;
        this.booking.venueTableId = null;
      }
    },
    async updateVenueSections() {
      if (this.activeVenueSetup.venueSections) {
        if (this.booking.venueSectionId) {
          let section = this.activeVenueSetup.venueSections.find(
            (sec) => sec.venueSectionId.id === this.booking.venueSectionId.id
          );
          if (section) {
            this.selectedSection = Object.assign(new VenueSection(), section);
            this.availableScoredTables = [];
            this.availableScoredTables = await this.getAvailableScoredTables();

          } else {
            this.selectedSection = null;
            this.booking.venueSectionId = undefined;
            this.availableScoredTables = [];
            this.selectedTable = null;
            this.selectedTableParts = null;
            this.selectedMinimumSpendAmount = null;
            this.booking.venueTableId = null;
          }
        }
      } else {
        this.selectedSection = null;
        this.booking.venueSectionId = undefined;
        this.availableScoredTables = [];
        this.selectedTable = null;
        this.selectedTableParts = null;
        this.selectedMinimumSpendAmount = null;
        this.booking.venueTableId = null;
      }
    },
    formatPrice(decAmount, currency) {
      var options = {};
      // These options are needed to round to whole numbers if that's what you want.
      //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
      //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)

      if (currency)
        _.assign(options, { style: "currency", currency: currency });
      var formatter = new Intl.NumberFormat("en-US", options);

      return formatter.format(decAmount); /* $2,500.00 */
    },
    tablePartsString() {
      return this.selectedTableParts.map(t => t.name).join(', ');
    }
  },
};
</script>

<style lang="scss" scoped>
</style>
